import {
  ConsequenceMatrixSelection,
  GeckoRiskAssessmentConfig,
  mapRiskClassToRiskScore,
} from 'types'
import {
  calculateConsequenceFromMatrixSelections,
  consequenceRowValueToPosition,
} from 'utils/fde/aiaFinancialConsequenceHelpers'

import { lookupRiskClass } from './riskMatrixHelpers'

export interface ConsequenceMatrixRiskResult {
  riskScore: number
  riskClass: string
  consequencePosition: number
}

export function consequencePositionFromSelections(
  selections: ConsequenceMatrixSelection[],
  config?: GeckoRiskAssessmentConfig
): number {
  if (!selections || selections.length === 0) return 0

  const rowLabels = config?.data.consequenceMatrix?.rowLabels || []
  const maxRowValue = calculateConsequenceFromMatrixSelections(selections)
  return rowLabels.length > 0
    ? consequenceRowValueToPosition(maxRowValue, rowLabels)
    : maxRowValue
}

/**
 * Pure function that computes a risk score from consequence matrix selections
 * and a likelihood value. Returns null when inputs are incomplete or out of bounds.
 *
 * Used by both RiskSelectionModal (local state) and useConsequenceMatrixRisk (form state).
 */
export function computeConsequenceMatrixRisk(
  selections: ConsequenceMatrixSelection[],
  likelihood: number,
  config?: GeckoRiskAssessmentConfig
): ConsequenceMatrixRiskResult | null {
  if (!selections || selections.length === 0 || likelihood <= 0) return null

  const consequencePosition = consequencePositionFromSelections(
    selections,
    config
  )
  if (consequencePosition <= 0) return null

  const riskClass = lookupRiskClass(likelihood, consequencePosition, config)
  if (riskClass === null) return null

  return {
    riskScore: mapRiskClassToRiskScore(riskClass, config),
    riskClass,
    consequencePosition,
  }
}
